/**
 * @fileoverview ProtectedRoute — authentication + RBAC route guard
 *
 * Usage:
 *   <ProtectedRoute>                          — require auth only
 *   <ProtectedRoute permission="finance.view"> — require auth + permission
 *   <ProtectedRoute anyOf={["finance.view","finance.admin"]}>
 *   <ProtectedRoute adminOnly>               — Admin / System Administrator only
 */

import PropTypes from "prop-types";
import { Navigate, useLocation } from "react-router-dom";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import { useAuth } from "context/AuthContext";

// Roles that may access Administration pages
const ADMIN_ROLES = ["System Administrator"];

function AccessDenied() {
  return (
    <SoftBox
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="80vh"
      textAlign="center"
      px={3}
    >
      <Icon sx={{ fontSize: "4rem !important", color: "error.main", mb: 2 }}>lock</Icon>
      <SoftTypography variant="h4" fontWeight="bold" color="dark" mb={1}>
        Access Denied
      </SoftTypography>
      <SoftTypography variant="body2" color="text" mb={3} maxWidth={400}>
        You don&apos;t have permission to access this section. Contact your system administrator
        if you believe this is an error.
      </SoftTypography>
      <SoftButton variant="gradient" color="info" component="a" href="/academy/dashboard">
        <Icon sx={{ mr: 0.5 }}>home</Icon>
        Back to Dashboard
      </SoftButton>
    </SoftBox>
  );
}

function AdminOnlyDenied() {
  return (
    <SoftBox
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="80vh"
      textAlign="center"
      px={3}
    >
      <SoftBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={88}
        height={88}
        borderRadius="50%"
        mb={3}
        sx={{
          background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
          boxShadow: "0 8px 24px rgba(26,115,232,0.35)",
        }}
      >
        <Icon sx={{ fontSize: "2.6rem !important", color: "#fff" }}>admin_panel_settings</Icon>
      </SoftBox>

      <SoftTypography variant="h4" fontWeight="bold" color="dark" mb={1}>
        Administration Access Only
      </SoftTypography>

      <SoftTypography
        variant="body2"
        color="text"
        mb={1}
        maxWidth={440}
        sx={{ lineHeight: 1.75 }}
      >
        This section is reserved for System Administrators. If you believe you need access,
        please reach out to your administrator to have your permissions updated.
      </SoftTypography>

      <SoftTypography variant="caption" color="text" mb={4} sx={{ opacity: 0.65 }}>
        Your current role does not include Administration privileges.
      </SoftTypography>

      <SoftButton variant="gradient" color="info" component="a" href="/academy/dashboard">
        <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
        Back to Academy
      </SoftButton>
    </SoftBox>
  );
}

export function ProtectedRoute({ children, permission, anyOf, adminOnly }) {
  const { isAuthenticated, loading, hasPermission, hasAnyPermission, currentUser } = useAuth();
  const location = useLocation();

  if (loading) return null;

  if (!isAuthenticated) {
    return <Navigate to="/authentication/sign-in" state={{ from: location }} replace />;
  }

  // Administration pages: only System Administrators
  if (adminOnly && !ADMIN_ROLES.includes(currentUser?.role)) return <AdminOnlyDenied />;

  if (permission && !hasPermission(permission)) return <AccessDenied />;
  if (anyOf && !hasAnyPermission(anyOf)) return <AccessDenied />;

  return children;
}

ProtectedRoute.defaultProps = {
  permission: null,
  anyOf: null,
  adminOnly: false,
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  permission: PropTypes.string,
  anyOf: PropTypes.arrayOf(PropTypes.string),
  adminOnly: PropTypes.bool,
};
